import { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlogHero from "../components/BlogHero";
import BlogCard from "../components/BlogCard";
import BlogSidebar from "../components/BlogSidebar";
import SEOHead from "../components/SEOHead";
import { BLOG_POSTS } from "../data/blogPosts";
import { ChevronLeft, ChevronRight, Tag, FolderOpen, Search } from "lucide-react";
import SkeletonCard from "../components/SkeletonCard";

import { useTranslation } from 'react-i18next';
const POSTS_PER_PAGE = 6;

function slugify(str) {
  return str.toLowerCase().replace(/\s+/g, "-");
}

export default function Blog() {
  const { t } = useTranslation();
  const { category: categoryParam, tag: tagParam } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(categoryParam ? decodeURIComponent(categoryParam) : "All");
  const [activeTag, setActiveTag] = useState(tagParam ? decodeURIComponent(tagParam) : null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Simulate data fetch for skeleton loading demonstration
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, [activeCategory, activeTag]);

  // Sync URL params to state
  useMemo(() => {
    if (categoryParam) {
      setActiveCategory(decodeURIComponent(categoryParam));
      setActiveTag(null);
      setCurrentPage(1);
    }
    if (tagParam) {
      setActiveTag(decodeURIComponent(tagParam));
      setActiveCategory("All");
      setCurrentPage(1);
    }
    if (!categoryParam && !tagParam) {
      // Only reset if we're on the base /blog route
    }
  }, [categoryParam, tagParam]);

  const categories = useMemo(() => {
    const cats = new Set(BLOG_POSTS.map((p) => p.category || p.tag));
    return ["All", ...Array.from(cats)];
  }, []);

  const allTags = useMemo(() => {
    const tags = new Set();
    BLOG_POSTS.forEach((p) => (p.tags || []).forEach((t) => tags.add(t)));
    return Array.from(tags);
  }, []);

  const filteredPosts = useMemo(() => {
    let posts = BLOG_POSTS;

    if (activeCategory !== "All") {
      posts = posts.filter(
        (p) => (p.category || p.tag) === activeCategory
      );
    }

    if (activeTag) {
      posts = posts.filter(
        (p) => (p.tags || []).includes(activeTag)
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(q)) ||
          (p.category || p.tag || "").toLowerCase().includes(q)
      );
    }

    return posts;
  }, [searchQuery, activeCategory, activeTag]);

  const showFeatured = searchQuery === "" && activeCategory === "All" && !activeTag && currentPage === 1;
  const featuredPost = showFeatured ? filteredPosts[0] : null;

  // Grid posts: skip featured if showing it, otherwise all filtered
  const gridSource = showFeatured ? filteredPosts.slice(1) : filteredPosts;
  const totalPages = Math.max(1, Math.ceil(gridSource.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const gridPosts = gridSource.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  );

  // Dynamic SEO title/description based on context
  const getSeoProps = () => {
    if (activeTag) {
      return {
        title: `Articles tagged "${activeTag}" | Max Employee Blog`,
        description: `Browse all articles tagged with ${activeTag} on the Max Employee blog. Insights, guides, and tutorials on AI digital workers.`,
        url: `/blog/tag/${slugify(activeTag)}`,
      };
    }
    if (activeCategory !== "All") {
      return {
        title: `${activeCategory} Articles | Max Employee Blog`,
        description: `Explore all ${activeCategory} articles on the Max Employee blog. Guides, insights, and case studies on AI digital workers.`,
        url: `/blog/category/${slugify(activeCategory)}`,
      };
    }
    return {
      title: "Blog — AI Agent Insights & Guides | Max Employee",
      description: "Learn how AI digital workers are transforming business. Guides, comparisons, case studies, and insights from the Max Employee team.",
      url: "/blog",
    };
  };

  const seoProps = getSeoProps();

  const breadcrumbItems = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
  ];
  if (activeCategory !== "All") {
    breadcrumbItems.push({ name: activeCategory, url: `/blog/category/${slugify(activeCategory)}` });
  }
  if (activeTag) {
    breadcrumbItems.push({ name: activeTag, url: `/blog/tag/${slugify(activeTag)}` });
  }

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url ? `https://maxemployee.com${item.url}` : undefined,
    })),
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setActiveTag(null);
    setCurrentPage(1);
  };

  const handleTagChange = (tag) => {
    setActiveTag(tag);
    setActiveCategory("All");
    setCurrentPage(1);
  };

  const handleSearch = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setActiveCategory("All");
    setActiveTag(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title="Blog | AI Agent Insights & Guides"
        description="Expert guides and insights on AI agents, automation, multi-agent workflows, and building AI-powered businesses."
        url={seoProps.url}
        structuredData={breadcrumbStructuredData}
      />
      <Header />
      <BlogHero />

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
        {/* Mobile search - shown above content on mobile */}
        <div className="lg:hidden mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              placeholder={t('blog.search_articles')}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-950/60 border border-zinc-800 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 min-h-[44px]"
              aria-label={t('blog.search_articles')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Main content */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {/* Active filter indicator */}
            {(activeCategory !== "All" || activeTag) && (
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Filtered by:
                </span>
                {activeCategory !== "All" && (
                  <span className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 px-2 sm:px-3 py-1 sm:py-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-500">
                    <FolderOpen className="w-3 h-3" />
                    {activeCategory}
                  </span>
                )}
                {activeTag && (
                  <span className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 px-2 sm:px-3 py-1 sm:py-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-500">
                    <Tag className="w-3 h-3" />
                    {activeTag}
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-zinc-500 hover:text-amber-500 font-mono text-[10px] uppercase tracking-wider transition-colors min-h-[44px] px-2"
                >{t('blog.clear_filters')}</button>
                <span className="font-mono text-[10px] text-zinc-500">
                  {filteredPosts.length} article{filteredPosts.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            {/* Category Filter Tabs - horizontal scroll on mobile */}
            <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0" role="tablist" aria-label={t('blog.category_filters')}>
              {categories.map((cat) => (
                <Link
                  key={cat}
                  role="tab"
                  aria-selected={activeCategory === cat && !activeTag}
                  to={cat === "All" ? "/blog" : `/blog/category/${slugify(cat)}`}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-3 sm:px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 whitespace-nowrap shrink-0 min-h-[44px] inline-flex items-center ${
                    activeCategory === cat && !activeTag
                      ? "bg-amber-500 text-black border-amber-500"
                      : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                  }`}
                >
                  {cat}
                </Link>
              ))}
            </div>

            {/* Tag pills - horizontal scroll on mobile */}
            {allTags.length > 0 && (
              <div className="flex gap-1.5 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" role="list" aria-label={t('blog.tags')}>
                {allTags.map((tag) => (
                  <Link
                    key={tag}
                    role="listitem"
                    to={`/blog/tag/${slugify(tag)}`}
                    onClick={() => handleTagChange(tag)}
                    className={`px-2 sm:px-3 py-1 font-mono text-[9px] uppercase tracking-wider border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 whitespace-nowrap shrink-0 min-h-[36px] inline-flex items-center ${
                      activeTag === tag
                        ? "bg-amber-500/20 text-amber-500 border-amber-500/50"
                        : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Featured Article Hero */}
            {featuredPost && (
              <div className="mb-6 sm:mb-8">
                <BlogCard post={featuredPost} featured />
              </div>
            )}

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog.loading_blog_posts')}>
                {featuredPost && <SkeletonCard featured />}
                {gridPosts.map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : gridPosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog.blog_posts')}>
                {gridPosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-20" role="status" aria-live="polite">
                <p className="font-display text-xl sm:text-2xl uppercase text-zinc-400 mb-4">{t('blog.no_articles_found')}</p>
                <p className="text-zinc-400 text-sm mb-6">{t('blog.try_adjusting_your_search_or_filter')}</p>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
                >{t('blog.view_all_articles')}</button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2 sm:gap-4 mt-8 sm:mt-12" aria-label={t('blog.pagination')}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog.previous_page')}
                >
                  <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t('blog.prev')}</span>
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog.next_page')}
                >
                  <span className="hidden sm:inline">{t('blog.next')}</span> <ChevronRight className="w-4 h-4" />
                </button>
              </nav>
            )}
          </div>

          {/* Sidebar - moved below content on mobile */}
          <aside className="lg:col-span-4 order-1 lg:order-2" aria-label={t('blog.blog_sidebar')}>
            <BlogSidebar
              onSearch={handleSearch}
              onCategoryChange={handleCategoryChange}
              activeCategory={activeCategory}
            />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
